import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { getTeachersBySchoolId, softDeleteTeacher, deleteTeacherArchived } from "../services/APIClient"; // Import both API calls

function RemovingTeacherModal({ show, onHide, schoolid, currentTeacherId, currentTeacherName }) {
  const [teachers, setTeachers] = useState([]); // Store teachers
  const [selectedTeacher, setSelectedTeacher] = useState(null); // Store the selected teacher
  const [deletionReason, setDeletionReason] = useState(""); // Store deletion reason
  const [confirmationVisible, setConfirmationVisible] = useState(false); // Toggle for confirmation
  const [statusMessage, setStatusMessage] = useState(""); // Status message for success/failure
  const [alertVariant, setAlertVariant] = useState(""); // Alert variant for Bootstrap
  const [isDeleting, setIsDeleting] = useState(false); // Loading state during deletion

  // Fetch teachers when the modal opens
  useEffect(() => {
    if (schoolid) {
      getTeachersBySchoolId(schoolid)
        .then((response) => {
          setTeachers(response); // Store the teachers
        })
        .catch((error) => {
          setStatusMessage("Error fetching teachers. Please try again.");
          setAlertVariant("danger");
        });
    }
  }, [schoolid]);

  // Handle teacher removal confirmation
  const handleRemoveTeacher = () => {
    // Validate that deletion reason is provided
    if (!deletionReason.trim()) {
      setStatusMessage("Please provide a reason for removing the teacher.");
      setAlertVariant("warning");
      return;
    }
    setConfirmationVisible(true); // Show the confirmation step
  };

  // Handle the actual delete API calls after confirmation
  const confirmRemoveTeacher = async () => {
    if (selectedTeacher && currentTeacherId && currentTeacherName) {
      setIsDeleting(true);
      
      try {
        // First, call the soft delete (existing functionality) - NOW WITH SCHOOLID
        await softDeleteTeacher(schoolid, selectedTeacher);
        
        // Then, call the archive delete with the required data - NOW WITH SCHOOLID
        await deleteTeacherArchived(schoolid, {
          teacherid: selectedTeacher,
          deletionReason: deletionReason,
          deletedByTeacherId: currentTeacherId,
          deletedByTeacherName: currentTeacherName
        });

        const teacherName = teachers.find(t => t.id === selectedTeacher)?.name;
        setStatusMessage(`Teacher "${teacherName}" was removed and archived successfully!`);
        setAlertVariant("success");
        
        // Update local state to remove the deleted teacher
        setTeachers(teachers.filter(t => t.id !== selectedTeacher));
        
        clearFields(); // Clear selection and reset the form
        autoClearMessage(); // Clear the message after a few seconds
        
      } catch (error) {
        console.error('Error removing teacher:', error);
        setStatusMessage("Failed to remove the teacher. Please try again.");
        setAlertVariant("danger");
      } finally {
        setIsDeleting(false);
        setConfirmationVisible(false); // Hide confirmation
      }
    }
  };

  // Automatically clear the status message after a few seconds
  const autoClearMessage = () => {
    setTimeout(() => {
      setStatusMessage("");
      setAlertVariant("");
    }, 5000); // Clear message after 5 seconds
  };

  // Clear the modal fields and messages
  const clearFields = () => {
    setSelectedTeacher(null);
    setDeletionReason("");
  };

  return (
    <Modal show={show} onHide={() => {
      onHide();
      clearFields(); // Clear the fields when modal is closed
      setConfirmationVisible(false); // Hide confirmation when modal closes
      setStatusMessage(""); // Clear any messages
    }}>
      <Modal.Header closeButton>
        <Modal.Title>Remove Teacher from School</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {/* Dropdown for selecting a teacher */}
        <Form.Group className="mb-3">
          <Form.Label>Select Teacher to Remove:</Form.Label>
          <Form.Control
            as="select"
            value={selectedTeacher || ""}
            onChange={(e) => setSelectedTeacher(e.target.value)}
            disabled={isDeleting}
          >
            <option value="">Select Teacher</option>
            {teachers.map((teacher) => (
              <option key={teacher.id} value={teacher.id}>
                {teacher.name}
              </option>
            ))}
          </Form.Control>
        </Form.Group>

        {/* Deletion reason input */}
        {selectedTeacher && (
          <Form.Group className="mb-3">
            <Form.Label>Reason for Removal: <span className="text-danger">*</span></Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Please provide a reason for removing this teacher..."
              value={deletionReason}
              onChange={(e) => setDeletionReason(e.target.value)}
              disabled={isDeleting}
            />
            <Form.Text className="text-muted">
              This reason will be recorded for audit purposes.
            </Form.Text>
          </Form.Group>
        )}

        {/* Remove button with teacher name */}
        {selectedTeacher && (
          <Button 
            variant="danger" 
            className="mt-2" 
            onClick={handleRemoveTeacher}
            disabled={isDeleting || !deletionReason.trim()}
          >
            Remove {teachers.find(t => t.id === selectedTeacher)?.name}
          </Button>
        )}

        {/* Confirmation message */}
        {confirmationVisible && (
          <div className="mt-3">
            <Alert variant="warning">
              <strong>Are you sure you want to remove teacher "
              {teachers.find(t => t.id === selectedTeacher)?.name}"?</strong>
              <br />
              <small>Reason: {deletionReason}</small>
              <br />
              <small className="text-muted">This action will permanently archive the teacher's record.</small>
            </Alert>
            <div className="d-flex gap-2">
              <Button 
                variant="danger" 
                onClick={confirmRemoveTeacher}
                disabled={isDeleting}
              >
                {isDeleting ? "Removing..." : "Confirm Remove"}
              </Button>
              <Button 
                variant="secondary" 
                onClick={() => setConfirmationVisible(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* Status message for success or failure */}
        {statusMessage && (
          <Alert 
            variant={alertVariant} 
            className="mt-3" 
            dismissible 
            onClose={() => setStatusMessage("")}
          >
            {statusMessage}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button 
          variant="secondary" 
          onClick={() => {
            onHide();
            clearFields(); // Clear fields when modal is closed
            setStatusMessage(""); // Clear any existing messages when modal is closed
            setConfirmationVisible(false);
          }}
          disabled={isDeleting}
        >
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default RemovingTeacherModal;